<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue'
  import type { DateRange } from 'radix-vue'
  import {
    Phone,
    Trash2,
    ExternalLink,
    MoreVertical,
    Settings,
    PhoneCall,
    Mail,
    Calendar,
    X,
    Search,
    ChevronDown,
    Check
  } from 'lucide-vue-next'
  import { TableCell, TableRow } from '@/components/ui/table'
  import { Button } from '@/components/ui/button'
  import { Input } from '@/components/ui/input'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { Badge } from '@/components/ui/badge'
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
  } from '@/components/ui/dialog'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import type { PaginatedResponse, FetchDataOptions } from '@/components/admin/PaginatedTable.vue'
  import {
    CallsService,
    type TCall,
    type CustomerCallResult,
    OrganisationsService,
    type CustomerOrganisation,
    ClassificationEnum,
    PhoneNumbersService,
    type CustomerPhoneNumber,
    UsersService,
    type TUser,
    RoleEnum
  } from '@/types/generated'
  import { useRoute, useRouter } from 'vue-router'
  import { useTranslation } from '@/composables/useTranslation'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import { ROUTES } from '@/router/routes.ts'
  import PhonebotSetupDialog from '@/components/app/calls/PhonebotSetupDialog.vue'
  import CustomerService from '@/components/svg/Illustrations/CustomerService.vue'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'
  import { RangeCalendar } from '@/components/ui/range-calendar'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import type { CalendarDate } from '@internationalized/date'

  const route = useRoute()
  const router = useRouter()

  const { translateLawField } = useLawFieldTranslation()
  const { translateLawTopic } = useLawTopicTranslation()

  const { t } = useTranslation({
    en: {
      Calls: 'Calls',
      TimeStamp: 'Time Stamp',
      Type: 'Type',
      Phone: 'Phone',
      FieldTopic: 'Field/Topic',
      Duration: 'Duration',
      Recording: 'Recording',
      Action: 'Action',
      ViewInquiry: 'Inquiry',
      Inquiry: 'Show inquiry',
      ViewDetails: 'Show details',
      Unknown: 'Unknown',
      pageDescription: 'Manage incoming calls and inquiries',
      NotCompleted: 'Not Completed',
      ExistingClient: 'Existing Client',
      NewClient: 'New Client',
      GeneralEnquiry: 'General Enquiry',
      Question: 'Question',
      LawFirmPartner: 'Law Firm Partner',
      Delete: 'Delete',
      HasInquiry: 'Has associated inquiry',
      CallForwarded: 'Call Forwarded',
      WithoutMessage: 'Without Message',
      Court: 'Court',
      Spam: 'Spam',
      AllTypes: 'All Types',
      LawField: 'Law Field',
      YourPhoneNumber: 'Your Phone Number',
      YourPhoneNumbers: 'Your Phone Numbers',
      GoToSettings: 'Go to Settings',
      Clerk: 'Clerk',
      notAssigned: 'Not assigned',
      emptyStateTitle: 'Set up your phone bot to get started',
      emptyStateDescription:
        'Your phone bot is not yet configured.\nComplete the setup to automatically handle calls.',
      setupNow: 'Set up now',
      TestCall: 'Test call',
      phoneDialogDescription: 'Call your number to test the phone AI yourself.',
      moreNumbers: '+{count} more',
      contactSupport: 'Contact support',
      allClerks: 'All Clerks',
      allLawFields: 'All Law Fields',
      all: 'All',
      clearAll: 'Clear all',
      timeframe: 'Timeframe',
      search: 'Search',
      noCallsFound: 'No calls found',
      noCallsYet: "You don't have any calls yet.",
      noResultsWithFilters: 'The current filters return no results.',
      resetFilters: 'Reset filters'
    },
    de: {
      Calls: 'Anrufe',
      TimeStamp: 'Eingegangen',
      Type: 'Typ',
      Phone: 'Telefonnummer',
      FieldTopic: 'Rechtsgebiet/Thema',
      Duration: 'Dauer',
      Recording: 'Aufnahme',
      Action: 'Aktion',
      ViewInquiry: 'Anfrage',
      Inquiry: 'Zur Anfrage',
      ViewDetails: 'Zum Anruf',
      Unknown: 'Unbekannt',
      pageDescription: 'Verwalten Sie eingehende Anrufe und Anfragen',
      NotCompleted: 'Unvollständig',
      ExistingClient: 'Bestandsmandant',
      NewClient: 'Neumandant',
      GeneralEnquiry: 'Allgemeine Anfrage',
      Question: 'Frage',
      LawFirmPartner: 'Geschäftspartner',
      Delete: 'Löschen',
      HasInquiry: 'Hat zugeordnete Anfrage',
      CallForwarded: 'Anruf weitergeleitet',
      WithoutMessage: 'Ohne Nachricht',
      Court: 'Gericht',
      Spam: 'Spam',
      AllTypes: 'Alle Typen',
      LawField: 'Rechtsgebiet',
      YourPhoneNumber: 'Ihre Telefonnummer',
      YourPhoneNumbers: 'Ihre Telefonnummern',
      GoToSettings: 'Zu den Einstellungen',
      Clerk: 'Sachbearbeiter',
      notAssigned: 'Nicht zugewiesen',
      emptyStateTitle: 'Richten Sie Ihren Phonebot ein, um zu beginnen',
      emptyStateDescription:
        'Ihr Phonebot ist noch nicht konfiguriert.\nVervollständigen Sie die Einrichtung, um Anrufe automatisch zu bearbeiten.',
      setupNow: 'Jetzt einrichten',
      TestCall: 'Testanruf',
      phoneDialogDescription: 'Rufen Sie Ihre Nummer an, um die Telefon-KI selbst zu testen.',
      moreNumbers: '+{count} mehr',
      contactSupport: 'Support kontaktieren',
      allClerks: 'Alle Sachbearbeiter',
      allLawFields: 'Alle Rechtsgebiete',
      all: 'Alle',
      clearAll: 'Alle löschen',
      timeframe: 'Zeitraum',
      search: 'Suchen',
      noCallsFound: 'Keine Anrufe gefunden',
      noCallsYet: 'Sie haben noch keine Anrufe.',
      noResultsWithFilters: 'Die aktuellen Filter liefern keine Ergebnisse.',
      resetFilters: 'Filter zurücksetzen'
    },
    fr: {
      Calls: 'Appels',
      TimeStamp: 'Horodatage',
      Type: 'Type',
      Phone: 'Téléphone',
      FieldTopic: 'Domaine/Sujet',
      Duration: 'Durée',
      Recording: 'Enregistrement',
      Action: 'Action',
      ViewInquiry: 'Demande',
      Inquiry: 'Voir la demande',
      ViewDetails: 'Voir les détails',
      Unknown: 'Inconnu',
      pageDescription: 'Gérer les appels entrants et les demandes',
      NotCompleted: 'Non terminé',
      ExistingClient: 'Client existant',
      NewClient: 'Nouveau client',
      GeneralEnquiry: 'Demande générale',
      Question: 'Question',
      LawFirmPartner: 'Partenaire du cabinet',
      Delete: 'Supprimer',
      HasInquiry: 'A une demande associée',
      CallForwarded: 'Appel transféré',
      WithoutMessage: 'Sans message',
      Court: 'Tribunal',
      Spam: 'Spam',
      AllTypes: 'Tous les types',
      LawField: 'Domaine juridique',
      YourPhoneNumber: 'Votre numéro de téléphone',
      YourPhoneNumbers: 'Vos numéros de téléphone',
      GoToSettings: 'Aller aux paramètres',
      Clerk: 'Collaborateur',
      notAssigned: 'Non attribué',
      emptyStateTitle: 'Configurez votre bot téléphonique pour commencer',
      emptyStateDescription:
        "Votre bot téléphonique n'est pas encore configuré.\nComplétez la configuration pour traiter automatiquement les appels.",
      setupNow: 'Configurer maintenant',
      TestCall: 'Appel test',
      phoneDialogDescription: "Appelez votre numéro pour tester vous-même l'IA téléphonique.",
      moreNumbers: '+{count} de plus',
      contactSupport: 'Contacter le support',
      allClerks: 'Tous les collaborateurs',
      allLawFields: 'Tous les domaines juridiques',
      all: 'Tous',
      clearAll: 'Tout effacer',
      timeframe: 'Période',
      search: 'Rechercher',
      noCallsFound: 'Aucun appel trouvé',
      noCallsYet: "Vous n'avez pas encore d'appels.",
      noResultsWithFilters: 'Les filtres actuels ne retournent aucun résultat.',
      resetFilters: 'Réinitialiser les filtres'
    }
  })

  const MAX_VISIBLE_NUMBERS = 3
  const showPhoneNumbersDialog = ref(false)
  const isVoiceStandAlone = ref(false)
  const isLoading = ref(false)
  const selectedLawField = ref('')
  const selectedClassifications = ref('')
  const selectedClerk = ref('')
  const searchQuery = ref('')
  const tableRef = ref()
  const org = ref<CustomerOrganisation>()
  const phoneNumbers = ref<CustomerPhoneNumber[]>([])
  const orgUsers = ref<TUser[]>([])
  const totalCalls = ref<number | null>(null)
  const dateRange = ref<DateRange>({
    start: undefined,
    end: undefined
  })

  // Dropdown open/search state
  const typeDropdownOpen = ref(false)
  const typeDropdownSearch = ref('')
  const lawFieldDropdownOpen = ref(false)
  const lawFieldDropdownSearch = ref('')
  const clerkDropdownOpen = ref(false)
  const clerkDropdownSearch = ref('')

  const initialPage = computed(() => {
    const page = Number(route.query.page)
    return Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1
  })

  const handlePageChange = (page: number) => {
    router.replace({ query: { ...route.query, page: page > 1 ? page.toString() : undefined } })
  }

  const classificationOptions = [
    { value: ClassificationEnum.EXISTING_CLIENT_CALL_BACK, label: 'ExistingClient' },
    { value: ClassificationEnum.NEW_CLIENT_CALL, label: 'NewClient' },
    { value: ClassificationEnum.GENERAL_ENQUIRY, label: 'GeneralEnquiry' },
    { value: ClassificationEnum.QUESTION, label: 'Question' },
    { value: ClassificationEnum.NOT_COMPLETED, label: 'NotCompleted' },
    { value: ClassificationEnum.LAWFIRM_PARTNER, label: 'LawFirmPartner' },
    { value: ClassificationEnum.CALL_FORWARDED, label: 'CallForwarded' },
    { value: ClassificationEnum.WITHOUT_MESSAGE, label: 'WithoutMessage' },
    { value: ClassificationEnum.COURT, label: 'Court' },
    { value: ClassificationEnum.SPAM, label: 'Spam' }
  ]

  const lawFieldOptions = computed(() => {
    if (!org.value?.law_fields) return []
    return (org.value.law_fields as Array<string>).map((value) => ({
      value,
      title: translateLawField(value)
    }))
  })

  const clerkOptions = computed(() => {
    return [
      { value: '', title: t('allClerks') },
      ...orgUsers.value
        .map((user) => ({
          value: user.uuid,
          title: `${user.first_name} ${user.last_name}`
        }))
        .sort((a, b) => a.title.localeCompare(b.title))
    ]
  })

  // Filtered dropdown options (search within dropdowns)
  const filteredTypeOptions = computed(() => {
    const search = typeDropdownSearch.value.toLowerCase()
    const opts = [
      { value: '', title: t('AllTypes') },
      ...classificationOptions
        .map((o) => ({ value: o.value, title: t(o.label) }))
        .sort((a, b) => a.title.localeCompare(b.title))
    ]
    if (!search) return opts
    return opts.filter((o) => o.title.toLowerCase().includes(search))
  })

  const filteredLawFieldOptions = computed(() => {
    const search = lawFieldDropdownSearch.value.toLowerCase()
    const opts = [{ value: '', title: t('allLawFields') }, ...lawFieldOptions.value]
    if (!search) return opts
    return opts.filter((o) => o.title.toLowerCase().includes(search))
  })

  const filteredClerkOptions = computed(() => {
    const search = clerkDropdownSearch.value.toLowerCase()
    if (!search) return clerkOptions.value
    return clerkOptions.value.filter((o) => o.title.toLowerCase().includes(search))
  })

  // Display labels for selected filter values
  const selectedTypeLabel = computed(() => {
    if (!selectedClassifications.value) return t('all')
    const selected = selectedClassifications.value.split(',')
    if (selected.length === 1) {
      const opt = classificationOptions.find((o) => o.value === selected[0])
      return opt ? t(opt.label) : t('all')
    }
    return `${selected.length}`
  })

  const selectedLawFieldLabel = computed(() => {
    if (!selectedLawField.value) return t('all')
    const opt = lawFieldOptions.value.find((o) => o.value === selectedLawField.value)
    return opt?.title || t('all')
  })

  const selectedClerkLabel = computed(() => {
    if (!selectedClerk.value) return t('all')
    const opt = clerkOptions.value.find((o) => o.value === selectedClerk.value)
    return opt?.title || t('all')
  })

  const hasActiveFilters = computed(() => {
    return (
      selectedClassifications.value !== '' ||
      selectedLawField.value !== '' ||
      selectedClerk.value !== '' ||
      searchQuery.value !== '' ||
      dateRange.value.start !== undefined ||
      dateRange.value.end !== undefined
    )
  })

  const clearFilters = () => {
    selectedClassifications.value = ''
    selectedLawField.value = ''
    selectedClerk.value = ''
    searchQuery.value = ''
    dateRange.value = { start: undefined, end: undefined }
  }

  // Selection handlers
  const selectType = (value: string) => {
    if (value === '') {
      selectedClassifications.value = ''
    } else {
      const current = selectedClassifications.value ? selectedClassifications.value.split(',') : []
      if (current.includes(value)) {
        const next = current.filter((v) => v !== value)
        selectedClassifications.value = next.join(',')
      } else {
        selectedClassifications.value = [...current, value].join(',')
      }
    }
  }

  const isTypeSelected = (value: string) => {
    if (!selectedClassifications.value) return false
    return selectedClassifications.value.split(',').includes(value)
  }

  const selectLawField = (value: string) => {
    selectedLawField.value = value
    lawFieldDropdownOpen.value = false
    lawFieldDropdownSearch.value = ''
  }

  const selectClerk = (value: string) => {
    selectedClerk.value = value
    clerkDropdownOpen.value = false
    clerkDropdownSearch.value = ''
  }

  const formatDateForDisplay = (date: CalendarDate | undefined) => {
    if (!date) return ''
    const jsDate = new Date(date.year, date.month - 1, date.day)
    return jsDate.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatDateForAPI = (dateObj: any) => {
    if (!dateObj) return undefined
    return `${dateObj.year}-${String(dateObj.month).padStart(2, '0')}-${String(dateObj.day).padStart(2, '0')}`
  }

  const dateRangeText = computed(() => {
    if (!dateRange.value.start && !dateRange.value.end) {
      return t('all')
    }
    const startDate = formatDateForDisplay(dateRange.value.start as CalendarDate)
    const endDate = formatDateForDisplay(dateRange.value.end as CalendarDate)
    if (
      dateRange.value.start &&
      dateRange.value.end &&
      dateRange.value.start.year === dateRange.value.end.year &&
      dateRange.value.start.month === dateRange.value.end.month &&
      dateRange.value.start.day === dateRange.value.end.day
    ) {
      return startDate
    }
    return `${startDate} - ${endDate}`
  })

  // Debounced search
  let searchTimeout: ReturnType<typeof setTimeout> | null = null
  watch(searchQuery, () => {
    if (searchTimeout) clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      if (tableRef.value) {
        tableRef.value.refreshData()
      }
    }, 300)
  })

  watch(
    [selectedLawField, selectedClassifications, selectedClerk, dateRange],
    () => {
      if (tableRef.value) {
        tableRef.value.refreshData()
      }
    },
    { deep: true }
  )

  const fetchCalls = async (options?: FetchDataOptions): Promise<PaginatedResponse<TCall>> => {
    try {
      isLoading.value = true

      const params: any = {
        limit: options?.limit,
        offset: options?.offset,
        search: searchQuery.value || undefined,
        resultDataLawField: selectedLawField.value || undefined,
        hasResult: true,
        clerk: selectedClerk.value || undefined,
        startDate: formatDateForAPI(dateRange.value.start),
        endDate: formatDateForAPI(dateRange.value.end),
        resultClassification: selectedClassifications.value || undefined
      }

      const response = await CallsService.callsList(params)

      totalCalls.value = response.count || 0

      return response as unknown as PaginatedResponse<TCall>
    } catch (error) {
      console.error('Error fetching calls:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const fetchCurrentOrg = async () => {
    try {
      const [orgResponse, userResponse] = await Promise.all([
        OrganisationsService.organisationsRetrieve({ uuid: 'current' }),
        UsersService.usersRetrieve({ uuid: 'current' })
      ])

      org.value = orgResponse as CustomerOrganisation

      isVoiceStandAlone.value =
        !org.value.enable_cases || (userResponse as any).role === RoleEnum.PHONE_AI_USER
    } catch (error) {
      console.error('Error fetching orgs:', error)
    }
  }

  const fetchOrgUsers = async () => {
    try {
      const response = await UsersService.usersList({ limit: 1000 })
      orgUsers.value = response.results as TUser[]
    } catch (error) {
      console.error('Error fetching org users:', error)
    }
  }

  const fetchPhoneNumbers = async () => {
    try {
      const response = await PhoneNumbersService.phoneNumbersList()
      phoneNumbers.value = response.results as CustomerPhoneNumber[]
    } catch (error) {
      console.error('Error fetching phone numbers:', error)
    }
  }

  const handleGoToSettings = () => {
    router.push(ROUTES.APP.SETTINGS.AI_PHONE_ANSWERING.PHONE_NUMBERS)
  }

  const handlePhonebotSetup = () => {
    handleGoToSettings()
  }

  onMounted(() => {
    fetchCurrentOrg()
    fetchPhoneNumbers()
    fetchOrgUsers()
  })

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-'
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const handleJumpToCase = (call: TCall) => {
    if (call.case_uuid) {
      window.location.href = ROUTES.APP.CASES.LEGACY.CASE_DETAIL.replace(':uuid', call.case_uuid)
    }
  }

  const handleViewDetails = (call: TCall) => {
    router.push(`/app/calls/${call.uuid}`)
  }

  const getStatusColor = (result: CustomerCallResult) => {
    if (!result) return 'bg-gray-50 text-gray-700 ring-gray-600/20'

    switch (result.classification) {
      case ClassificationEnum.EXISTING_CLIENT_CALL_BACK:
        return 'bg-purple-50 text-purple-700 ring-purple-600/20'

      case ClassificationEnum.NEW_CLIENT_CALL:
        return 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'

      case ClassificationEnum.GENERAL_ENQUIRY:
        return 'bg-sky-50 text-sky-700 ring-sky-600/20'

      case ClassificationEnum.QUESTION:
        return 'bg-amber-50 text-amber-700 ring-amber-600/20'

      case ClassificationEnum.NOT_COMPLETED:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20'

      case ClassificationEnum.LAWFIRM_PARTNER:
        return 'bg-indigo-50 text-indigo-700 ring-indigo-600/20'

      case ClassificationEnum.WITHOUT_MESSAGE:
        return 'bg-red-50 text-red-700 ring-red-600/20'

      case ClassificationEnum.COURT:
        return 'bg-red-100 text-red-800 ring-red-700/30'

      case ClassificationEnum.SPAM:
        return 'bg-gray-100 text-gray-800 ring-gray-700/30'

      default:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20'
    }
  }

  const classificationLabels = {
    EXISTING_CLIENT_CALL_BACK: `${t('ExistingClient')}`,
    NEW_CLIENT_CALL: `${t('NewClient')}`,
    GENERAL_ENQUIRY: `${t('GeneralEnquiry')}`,
    QUESTION: `${t('Question')}`,
    NOT_COMPLETED: `${t('NotCompleted')}`,
    LAWFIRM_PARTNER: `${t('LawFirmPartner')}`,
    CALL_FORWARDED: `${t('CallForwarded')}`,
    WITHOUT_MESSAGE: `${t('WithoutMessage')}`,
    COURT: `${t('Court')}`,
    SPAM: `${t('Spam')}`
  }

  type ClassificationType = keyof typeof classificationLabels

  const getClassificationLabel = (classification: ClassificationType | undefined) => {
    if (!classification) return t('Unknown')
    const resp = classificationLabels[classification]
    return resp
  }

  // Direct deletion function
  const deleteCall = async (call: TCall) => {
    try {
      await CallsService.callsDestroy({
        uuid: call.uuid
      })

      // Refresh the table data
      if (tableRef.value) {
        tableRef.value.refreshData()
      }
    } catch (error) {
      console.error('Error deleting call:', error)
    }
  }

  // get clerks for the call. Case clerks are preferred
  const getCallClerks = (call: TCall) => {
    const caseClerks = (call as any).case?.clerks
    if (caseClerks && caseClerks.length > 0) return caseClerks
    const callClerks = (call as any).clerks
    if (callClerks && callClerks.length > 0) return callClerks
    // Fallback to single clerk for backward compat
    const singleClerk = call.case?.clerk || call.clerk
    return singleClerk ? [singleClerk] : []
  }
</script>

<template>
  <SpaCompatComponent>
    <div class="min-h-screen pb-20 pt-5">
      <div class="mx-auto space-y-6">
        <!-- Header -->
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-semibold text-gray-900">{{ t('Calls') }}</h1>
            <p class="mt-1 text-sm text-gray-500">{{ t('pageDescription') }}</p>
          </div>
          <a
            href="mailto:support@jupus.de"
            class="inline-flex items-center gap-2 rounded-md px-1.5 py-1.5 text-sm font-medium text-gray-800
              hover:text-purple-600"
          >
            <Mail class="h-5 w-5" />
            {{ t('contactSupport') }}
          </a>
        </div>

        <!-- Phone Numbers Card -->
        <div
          v-if="phoneNumbers.length > 0"
          class="rounded-xl border border-gray-200 bg-white p-4"
        >
          <div class="flex items-center justify-between gap-4">
            <h2 class="text-base font-semibold text-gray-900">
              {{ phoneNumbers.length === 1 ? t('YourPhoneNumber') : t('YourPhoneNumbers') }}
            </h2>
            <div class="flex items-center gap-2">
              <Button
                variant="default"
                size="sm"
                class="border-1 h-8 gap-1.5 rounded-md border-gray-300 bg-white px-1.5 text-sm font-normal text-black
                  shadow-md hover:text-white"
                @click="handleGoToSettings"
              >
                <Settings class="h-4 w-4" />
                {{ t('GoToSettings') }}
              </Button>
              <Button
                variant="default"
                size="sm"
                class="border-1 h-8 gap-1.5 rounded-md border-gray-300 bg-white px-1.5 text-sm font-normal text-black
                  shadow-md hover:text-white"
                @click="showPhoneNumbersDialog = true"
              >
                <PhoneCall class="h-4 w-4" />
                {{ t('TestCall') }}
              </Button>
            </div>
          </div>
          <div class="mt-4 flex flex-wrap items-center gap-4">
            <a
              v-for="phoneNumber in phoneNumbers.slice(0, MAX_VISIBLE_NUMBERS)"
              :key="phoneNumber.uuid"
              :href="`tel:${phoneNumber.phone_number}`"
              class="cursor-pointer text-sm text-gray-900 underline decoration-gray-500 underline-offset-2
                hover:text-purple-600 hover:decoration-purple-600"
            >
              {{ phoneNumber.phone_number }}
            </a>
            <button
              v-if="phoneNumbers.length > MAX_VISIBLE_NUMBERS"
              class="text-sm font-medium text-purple-600 hover:text-purple-700"
              @click="showPhoneNumbersDialog = true"
            >
              {{
                t('moreNumbers').replace(
                  '{count}',
                  String(phoneNumbers.length - MAX_VISIBLE_NUMBERS)
                )
              }}
            </button>
          </div>
        </div>

        <!-- Table -->
        <div class="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-900/5">
          <!-- Show paginated table if not standalone or loading or there are calls or there are no calls and there are filters-->
          <template
            v-if="
              !isVoiceStandAlone ||
              isLoading ||
              (totalCalls !== null && totalCalls > 0) ||
              (totalCalls === 0 && hasActiveFilters)
            "
          >
            <!-- Filters Row -->
            <div class="mb-2 flex flex-col gap-2 md:flex-row md:items-center">
              <!-- Search input -->
              <div class="relative w-full md:max-w-[320px] md:flex-1">
                <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  v-model="searchQuery"
                  type="text"
                  :placeholder="t('search')"
                  class="pl-9"
                  autocomplete="off"
                  name="call-filter-query"
                />
              </div>

              <!-- Filter controls wrapper -->
              <div class="flex flex-wrap items-center gap-2">
                <!-- Type (classification) multi-select dropdown -->
                <Popover v-model:open="typeDropdownOpen">
                  <PopoverTrigger asChild>
                    <button
                      class="inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2
                        text-sm hover:bg-gray-50"
                    >
                      <span class="shrink-0 text-gray-500">{{ t('Type') }}:</span>
                      <span class="truncate font-medium text-gray-900">
                        {{ selectedClassifications ? selectedTypeLabel : t('all') }}
                      </span>
                      <Badge
                        v-if="
                          selectedClassifications && selectedClassifications.split(',').length > 1
                        "
                        class="ml-0.5 h-5 min-w-[20px] bg-purple-100 px-1.5 text-xs text-purple-700"
                      >
                        +{{ selectedClassifications.split(',').length - 1 }}
                      </Badge>
                      <ChevronDown class="h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    class="w-[240px] p-0"
                    align="start"
                  >
                    <div class="p-2">
                      <div class="relative">
                        <Search
                          class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        />
                        <Input
                          v-model="typeDropdownSearch"
                          type="text"
                          :placeholder="t('search')"
                          class="h-8 pl-8 text-sm"
                          autocomplete="off"
                        />
                      </div>
                    </div>
                    <div class="max-h-[200px] overflow-y-auto border-t border-gray-100">
                      <button
                        v-for="option in filteredTypeOptions"
                        :key="option.value"
                        class="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                        :class="{
                          'bg-gray-50':
                            option.value === ''
                              ? !selectedClassifications
                              : isTypeSelected(option.value)
                        }"
                        @click="selectType(option.value)"
                      >
                        <span class="truncate">{{ option.title }}</span>
                        <Check
                          v-if="
                            option.value === ''
                              ? !selectedClassifications
                              : isTypeSelected(option.value)
                          "
                          class="h-4 w-4 shrink-0 text-purple-600"
                        />
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>

                <!-- Law field filter dropdown -->
                <Popover v-model:open="lawFieldDropdownOpen">
                  <PopoverTrigger asChild>
                    <button
                      class="inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2
                        text-sm hover:bg-gray-50"
                    >
                      <span class="shrink-0 text-gray-500">{{ t('LawField') }}:</span>
                      <span class="truncate font-medium text-gray-900">
                        {{ selectedLawFieldLabel }}
                      </span>
                      <ChevronDown class="h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    class="w-[240px] p-0"
                    align="start"
                  >
                    <div class="p-2">
                      <div class="relative">
                        <Search
                          class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        />
                        <Input
                          v-model="lawFieldDropdownSearch"
                          type="text"
                          :placeholder="t('search')"
                          class="h-8 pl-8 text-sm"
                          autocomplete="off"
                        />
                      </div>
                    </div>
                    <div class="max-h-[200px] overflow-y-auto border-t border-gray-100">
                      <button
                        v-for="option in filteredLawFieldOptions"
                        :key="option.value"
                        class="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                        :class="{ 'bg-gray-50': selectedLawField === option.value }"
                        :title="option.title"
                        @click="selectLawField(option.value)"
                      >
                        <span class="truncate">{{ option.title }}</span>
                        <Check
                          v-if="selectedLawField === option.value"
                          class="h-4 w-4 shrink-0 text-purple-600"
                        />
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>

                <!-- Clerk filter dropdown -->
                <Popover v-model:open="clerkDropdownOpen">
                  <PopoverTrigger asChild>
                    <button
                      class="inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2
                        text-sm hover:bg-gray-50"
                    >
                      <span class="shrink-0 text-gray-500">{{ t('Clerk') }}:</span>
                      <span class="truncate font-medium text-gray-900">
                        {{ selectedClerkLabel }}
                      </span>
                      <ChevronDown class="h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    class="w-[240px] p-0"
                    align="start"
                  >
                    <div class="p-2">
                      <div class="relative">
                        <Search
                          class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        />
                        <Input
                          v-model="clerkDropdownSearch"
                          type="text"
                          :placeholder="t('search')"
                          class="h-8 pl-8 text-sm"
                          autocomplete="off"
                        />
                      </div>
                    </div>
                    <div class="max-h-[200px] overflow-y-auto border-t border-gray-100">
                      <button
                        v-for="option in filteredClerkOptions"
                        :key="option.value"
                        class="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                        :class="{ 'bg-gray-50': selectedClerk === option.value }"
                        :title="option.title"
                        @click="selectClerk(option.value)"
                      >
                        <span class="truncate">{{ option.title }}</span>
                        <Check
                          v-if="selectedClerk === option.value"
                          class="h-4 w-4 shrink-0 text-purple-600"
                        />
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>

                <!-- Date range filter -->
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      class="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm
                        hover:bg-gray-50"
                    >
                      <Calendar class="h-4 w-4 shrink-0 text-gray-500" />
                      <span class="shrink-0 text-gray-500">{{ t('timeframe') }}:</span>
                      <span class="font-medium text-gray-900">{{ dateRangeText }}</span>
                      <X
                        v-if="dateRange.start || dateRange.end"
                        class="h-4 w-4 shrink-0 rounded-sm text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                        @click.stop="dateRange = { start: undefined, end: undefined }"
                      />
                      <ChevronDown
                        v-else
                        class="h-4 w-4 shrink-0 text-gray-400"
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start">
                    <RangeCalendar v-model="dateRange" />
                  </PopoverContent>
                </Popover>

                <!-- Spacer -->
                <div class="hidden flex-1 md:block"></div>

                <!-- Clear all link -->
                <button
                  class="whitespace-nowrap text-sm text-gray-600 underline hover:text-gray-900"
                  :class="{ invisible: !hasActiveFilters }"
                  @click="clearFilters"
                >
                  {{ t('clearAll') }}
                </button>
              </div>
            </div>

            <PaginatedTableView
              ref="tableRef"
              :fetchData="fetchCalls"
              :initial-page="initialPage"
              @page-change="handlePageChange"
            >
              <template #table-head>
                <TableCell class="w-[140px] py-4">{{ t('TimeStamp') }}</TableCell>
                <TableCell class="w-[140px] py-4">{{ t('Type') }}</TableCell>
                <TableCell class="w-[180px] py-4">Name</TableCell>
                <TableCell class="w-[160px] py-4">{{ t('Phone') }}</TableCell>
                <TableCell class="w-[120px] py-4">{{ t('FieldTopic') }}</TableCell>
                <TableCell class="w-[100px] py-4">{{ t('Duration') }}</TableCell>
                <TableCell class="w-[160px] py-4">{{ t('Clerk') }}</TableCell>
                <TableCell
                  v-if="!isVoiceStandAlone"
                  class="py-4"
                >
                  {{ t('ViewInquiry') }}
                </TableCell>
                <TableCell class="py-4"></TableCell>
              </template>

              <template #table-row="{ items }">
                <TableRow
                  v-for="call in items"
                  :key="call.uuid"
                  class="group cursor-pointer border-b border-gray-100 hover:bg-gray-50/50"
                  @click="
                    call.case_uuid && !isVoiceStandAlone
                      ? handleJumpToCase(call)
                      : handleViewDetails(call)
                  "
                >
                  <TableCell class="whitespace-nowrap py-4 text-sm text-gray-600">
                    {{ formatDate(call.call_start_time) }}
                  </TableCell>
                  <TableCell>
                    <Badge :class="`${getStatusColor(call.result)} whitespace-nowrap`">
                      {{ getClassificationLabel(call.result?.classification) || t('Unknown') }}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div class="font-medium text-gray-900">
                      {{
                        `${call.result?.data?.client?.first_name ?? ''} ${call.result?.data?.client?.last_name ?? ''}`.trim() ||
                        '_'
                      }}
                    </div>
                  </TableCell>
                  <TableCell class="whitespace-nowrap">
                    <div class="flex items-center gap-2 text-sm text-gray-600">
                      <Phone class="h-3 w-3 text-gray-400" />
                      {{ call.result?.data?.client?.phone || '-' }}
                    </div>
                  </TableCell>
                  <TableCell class="text-xs text-gray-600">
                    {{ translateLawField(call.result?.data?.law?.field) }}
                    <br />
                    {{ translateLawTopic(call.result?.data?.law?.topic) }}
                  </TableCell>
                  <TableCell class="text-sm text-gray-600">
                    {{ formatDuration(call.duration) }}
                  </TableCell>
                  <TableCell>
                    <div
                      v-if="getCallClerks(call).length > 0"
                      class="flex flex-wrap items-center gap-1"
                    >
                      <div
                        v-for="(clerk, idx) in getCallClerks(call).slice(0, 1)"
                        :key="clerk.uuid || idx"
                        class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1"
                      >
                        <div
                          class="flex h-6 w-6 items-center justify-center rounded-full bg-purple-500 text-sm text-white"
                        >
                          {{
                            (clerk.first_name?.charAt(0) || '') + (clerk.last_name?.charAt(0) || '')
                          }}
                        </div>
                        <span class="max-w-[100px] truncate text-sm">
                          {{ clerk.first_name + ' ' + clerk.last_name }}
                        </span>
                      </div>
                      <span
                        v-if="getCallClerks(call).length > 1"
                        class="rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-600"
                      >
                        +{{ getCallClerks(call).length - 1 }}
                      </span>
                    </div>
                    <span
                      v-else
                      class="text-sm text-gray-500"
                    >
                      {{ t('notAssigned') }}
                    </span>
                  </TableCell>
                  <TableCell
                    v-if="!isVoiceStandAlone"
                    class="pr-4"
                  >
                    <div class="flex items-center justify-end gap-2">
                      <!-- Only show indicator when there's a case_uuid -->
                      <Button
                        v-if="call.case_uuid"
                        variant="secondary"
                        class="flex h-8 min-w-[122px] items-center gap-1 border border-gray-300 bg-white text-sm hover:bg-gray-200"
                        @click.stop="handleJumpToCase(call)"
                      >
                        <ExternalLink class="h-3 w-3" />
                        {{ t('Inquiry') }}
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <!-- Dropdown menu for other actions -->
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          class="h-8 w-8"
                          @click.stop
                        >
                          <MoreVertical class="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <!-- Delete option for all calls -->
                        <DropdownMenuItem
                          class="text-red-500 focus:bg-red-50 focus:text-red-600"
                          @click="deleteCall(call)"
                        >
                          <Trash2 class="mr-2 h-4 w-4" />
                          {{ t('Delete') }}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              </template>

              <!-- Custom empty state -->
              <template #empty>
                <div class="mx-auto max-w-md text-center">
                  <div
                    class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100"
                  >
                    <Search class="h-6 w-6 text-gray-400" />
                  </div>
                  <p class="text-base font-medium text-gray-900">
                    {{ t('noCallsFound') }}
                  </p>
                  <p class="mt-1 text-sm text-gray-500">
                    <template v-if="hasActiveFilters">
                      {{ t('noResultsWithFilters') }}
                    </template>
                    <template v-else>
                      {{ t('noCallsYet') }}
                    </template>
                  </p>

                  <!-- Clear filters button if filters are active -->
                  <div
                    v-if="hasActiveFilters"
                    class="mt-4"
                  >
                    <button
                      class="text-sm text-purple-600 hover:text-purple-700 hover:underline"
                      @click="clearFilters"
                    >
                      {{ t('resetFilters') }}
                    </button>
                  </div>
                </div>
              </template>
            </PaginatedTableView>
          </template>
          <div
            v-else-if="isVoiceStandAlone && !isLoading"
            class="flex flex-col items-center justify-center py-16"
          >
            <!-- Illustration -->
            <CustomerService class="mb-8 h-64 w-64" />

            <!-- Title -->
            <h2 class="mb-3 text-center text-xl font-semibold text-gray-900">
              {{ t('emptyStateTitle') }}
            </h2>

            <!-- Description -->
            <p
              class="mb-8 max-w-md whitespace-pre-line text-center text-base font-normal leading-6 tracking-normal
                text-gray-600"
            >
              {{ t('emptyStateDescription') }}
            </p>

            <!-- Setup Button -->
            <Button
              size="lg"
              class="bg-purple-600 hover:bg-purple-700"
              @click="handlePhonebotSetup"
            >
              {{ t('setupNow') }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </SpaCompatComponent>

  <!-- Setup Phonebot Dialog -->
  <PhonebotSetupDialog
    v-if="isVoiceStandAlone"
    :total-calls="totalCalls"
    @setup="handlePhonebotSetup"
  />

  <!-- Phone Numbers Dialog -->
  <Dialog
    :open="showPhoneNumbersDialog"
    @update:open="showPhoneNumbersDialog = $event"
  >
    <DialogContent class="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>
          {{ phoneNumbers.length === 1 ? t('YourPhoneNumber') : t('YourPhoneNumbers') }}
        </DialogTitle>
        <DialogDescription>
          {{ t('phoneDialogDescription') }}
        </DialogDescription>
      </DialogHeader>
      <div class="grid grid-cols-2 gap-3">
        <div
          v-for="phoneNumber in phoneNumbers"
          :key="phoneNumber.uuid"
          class="rounded-lg border border-gray-200 p-4"
        >
          <div class="text-sm text-gray-500">
            {{ phoneNumber.lawfirm_name || '' }}
          </div>
          <a
            :href="`tel:${phoneNumber.phone_number}`"
            class="text-base font-semibold text-gray-900 underline"
          >
            {{ phoneNumber.phone_number }}
          </a>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
